import { cloneElement, isValidElement, useId, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[var(--scrim)] p-3 pt-[6vh] backdrop-blur-sm sm:p-4 sm:pt-[10vh]">
      <div className={cn('flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-[16px] border border-token bg-token-raised shadow-[0_30px_60px_var(--shadow-color)]', wide ? 'max-w-2xl' : 'max-w-md')}>
        <div className="flex flex-none items-center justify-between border-b border-token-subtle px-5 py-3.5">
          <h2 className="font-display text-[15px] font-bold tracking-[-0.01em] text-token-primary">{title}</h2>
          <button onClick={onClose} className="text-token-muted transition-colors hover:text-token-primary">
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex flex-none justify-end gap-2 border-t border-token-subtle px-5 py-3.5">{footer}</div>}
      </div>
    </div>
  )
}

export function Field({ label, children, className }: { label?: string; children: ReactNode; className?: string }) {
  const labelId = useId()
  const labelledChildren = label && isValidElement<{ 'aria-labelledby'?: string }>(children)
    ? cloneElement(children, { 'aria-labelledby': labelId })
    : children
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && <span id={labelId} className="block text-[13px] font-semibold text-token-secondary">{label}</span>}
      {labelledChildren}
    </div>
  )
}

const fieldClass =
  'h-9 w-full rounded-[10px] border border-token bg-token-surface px-3 text-[13.5px] text-token-primary outline-none transition-colors placeholder:text-token-muted focus:border-token-strong [@media(pointer:coarse)]:h-11' +
  // A disabled field that looks editable is a field people type into and then wonder about. On a
  // signed SWMS that state IS the message, so it has to be visible without reading a banner.
  ' disabled:cursor-not-allowed disabled:border-token-subtle disabled:bg-token-disabled disabled:text-token-secondary'

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn(fieldClass, props.className)} />
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        'w-full rounded-[10px] border border-token bg-token-surface px-3 py-2 text-[13.5px] text-token-primary outline-none transition-colors placeholder:text-token-muted focus:border-token-strong [@media(pointer:coarse)]:min-h-[44px]' +
        ' disabled:cursor-not-allowed disabled:border-token-subtle disabled:bg-token-disabled disabled:text-token-secondary',
        props.className,
      )}
    />
  )
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cn(fieldClass, props.className)} />
}
